import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { lazy, Suspense, Component } from "react";
import Login from "./pages/Login.jsx";
import VerifyMagic from "./pages/VerifyMagic.jsx";
import VerifyEmail from "./pages/VerifyEmail.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import SharePublica from "./pages/SharePublica.jsx";
import Layout from "./components/Layout.jsx";
import CuentaLayout from "./components/CuentaLayout.jsx";
import ResultadosLayout from "./components/ResultadosLayout.jsx";
import PlanLayout from "./components/PlanLayout.jsx";
import TrabajoLayout from "./components/TrabajoLayout.jsx";
import BrandLoader from "./components/BrandLoader.jsx";
import PageSkeleton from "./components/PageSkeleton.jsx";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import { SwUpdateBanner } from "./components/SwUpdateBanner.jsx";
import { InstallBanner }  from "./components/InstallBanner.jsx";
import { Portal404, PortalMantenimientoGate, AvisoSinConexion, PortalError } from "./components/sistema/PaginaPortal.jsx";

// Lazy load · code splitting por ruta (cada página queda en chunk separado)
const SelectCliente = lazy(() => import("./pages/SelectCliente.jsx"));
const Dashboard     = lazy(() => import("./pages/Dashboard.jsx"));
const Tareas        = lazy(() => import("./pages/Tareas.jsx"));
const TareaDetalle  = lazy(() => import("./pages/TareaDetalle.jsx"));
const Pedidos       = lazy(() => import("./pages/Pedidos.jsx"));
const Calendario    = lazy(() => import("./pages/Calendario.jsx"));
const Galeria       = lazy(() => import("./pages/Galeria.jsx"));
const FeedPreview   = lazy(() => import("./pages/FeedPreview.jsx"));
const Reportes      = lazy(() => import("./pages/Reportes.jsx"));
const Facturas      = lazy(() => import("./pages/Facturas.jsx"));
const Equipo        = lazy(() => import("./pages/Equipo.jsx"));
const Brand         = lazy(() => import("./pages/Brand.jsx"));
const Documentos    = lazy(() => import("./pages/Documentos.jsx"));
const Favoritos     = lazy(() => import("./pages/Favoritos.jsx"));
const AprobarCarousel = lazy(() => import("./pages/AprobarCarousel.jsx"));
const Configuracion = lazy(() => import("./pages/Configuracion.jsx"));
const Conversacion  = lazy(() => import("./pages/Conversacion.jsx"));  // v8.5.26 · F3
const Coberturas    = lazy(() => import("./pages/Coberturas.jsx"));    // v7.6.1 S3.1
const Presentaciones = lazy(() => import("./pages/Presentaciones.jsx"));
const Planes        = lazy(() => import("./pages/Planes.jsx"));        // v8.1.0
const Campanas        = lazy(() => import("./pages/Campanas.jsx"));       // v8.3.8
const CampanaDetalle  = lazy(() => import("./pages/CampanaDetalle.jsx")); // v8.3.8
const PlanDetalle   = lazy(() => import("./pages/PlanDetalle.jsx"));   // v8.1.0

// v7.6.1 · Detecta el error clásico "Failed to fetch dynamically imported module"
// que aparece cuando el browser tiene el index.html viejo cacheado apuntando a chunks
// con hashes que ya no existen tras un redeploy.
function isStaleChunkError(err) {
  const msg = String(err?.message || err || "");
  return /Failed to fetch dynamically imported module|Loading chunk .* failed|Importing a module script failed/i.test(msg);
}

async function hardReload() {
  // Bust SW cache si es posible + reload sin cache
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
    }
  } catch {}
  // reload() sin argumentos en browsers modernos igual respeta el CC del server;
  // agregamos un query param para forzar bypass
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString(36));
  window.location.replace(url.toString());
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null, autoReloading: false }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) {
    // Auto-reload en stale chunk (una sola vez por sesión para evitar loops)
    if (isStaleChunkError(error) && !sessionStorage.getItem("__auto_reloaded")) {
      sessionStorage.setItem("__auto_reloaded", "1");
      this.setState({ autoReloading: true });
      hardReload();
    }
  }
  render() {
    if (this.state.error) {
      const stale = isStaleChunkError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--paper)] p-6">
          <div className="max-w-sm w-full bg-white rounded-[20px] p-7 text-center shadow-overlay">
            <p className="text-lg font-extrabold text-[var(--ink)] mb-1">
              {this.state.autoReloading ? "Actualizando…" : stale ? "Nueva versión disponible" : "Algo salió mal"}
            </p>
            <p className="text-sm text-[var(--muted)] mb-4">
              {this.state.autoReloading
                ? "Cargando la última versión del portal…"
                : stale
                  ? "Tocá recargar para traer los cambios más recientes."
                  : (this.state.error?.message || "Error inesperado. El equipo ya fue notificado.")}
            </p>
            <button
              onClick={() => { sessionStorage.removeItem("__auto_reloaded"); hardReload(); }}
              className="h-11 px-6 bg-[var(--ink)] hover:opacity-90 text-[var(--surface)] rounded-full text-sm font-extrabold transition-opacity"
            >
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// v8.5.30 · F3 · redirect que conserva el query string — /calendario?dia=...
// y /feed-preview?periodo=... siguen resolviendo desde afuera (notifs, links
// viejos) pero ahora entran a la zona Plan sin perder el parámetro.
function RedirectPreserveQuery({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// v8.5.31 · F3 · /campanas/:id vivía suelto; ahora vive en /tareas/campanas/:id.
function CampanaIdRedirect() {
  const { id } = useParams();
  return <Navigate to={`/tareas/campanas/${id}`} replace />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BrandLoader mensaje="Cargando tu portal…"/>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Suspense fallback usado por todas las routes lazy
function RouteSuspense({ children }) {
  return <Suspense fallback={<PageSkeleton/>}>{children}</Suspense>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <SwUpdateBanner />
      <InstallBanner />
      <AvisoSinConexion />
      <PortalMantenimientoGate />
      <Routes>
        {/* Rutas públicas (no lazy — son las primeras que ve el usuario) */}
        <Route path="/login"               element={<Login />} />
        <Route path="/auth/verify"         element={<VerifyMagic />} />
        <Route path="/auth/verify-email"   element={<VerifyEmail />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/share/:token"        element={<SharePublica />} />

        {/* Protegidas + lazy */}
        <Route path="/elegir-cliente" element={<Protected><RouteSuspense><SelectCliente /></RouteSuspense></Protected>} />
        <Route path="/"               element={<Protected><Layout /></Protected>}>
          <Route index                    element={<RouteSuspense><Dashboard /></RouteSuspense>} />
          {/* v8.5.31 · F3 · zona "Trabajo" — Campañas y Favoritos eran rutas
              top-level sin relación visible con Tareas, pese a ser otras
              formas de ver el mismo trabajo (agrupado por campaña / filtrado
              a lo guardado). Ahora comparten pestañas bajo /tareas/*, mismo
              patrón que Resultados y Plan. Aprobar y la ficha de tarea NO
              entran en el shell — son modos full-screen propios (carrusel
              inmersivo / ficha con su propia navegación), no vistas de lista. */}
          <Route path="tareas" element={<RouteSuspense><TrabajoLayout /></RouteSuspense>}>
            <Route index               element={<Tareas />} />
            <Route path="pedidos"      element={<Pedidos />} />
            <Route path="campanas"     element={<Campanas />} />
            <Route path="campanas/:id" element={<CampanaDetalle />} />
            <Route path="favoritos"    element={<Favoritos />} />
          </Route>
          {/* v8.5.25 · F3 · el carrusel de aprobación vive bajo /tareas/*
              (era /aprobar suelto, invisible en mobile). */}
          <Route path="tareas/aprobar"    element={<RouteSuspense><AprobarCarousel /></RouteSuspense>} />
          <Route path="tareas/aprobar-masivo" element={<Navigate to="/tareas/aprobar" replace />} />
          <Route path="tareas/:id"        element={<RouteSuspense><TareaDetalle /></RouteSuspense>} />
          <Route path="campanas"          element={<Navigate to="/tareas/campanas" replace />} />
          <Route path="campanas/:id"      element={<CampanaIdRedirect />} />
          <Route path="favoritos"         element={<Navigate to="/tareas/favoritos" replace />} />

          {/* v8.5.24 · F3 · zona "Resultados" — Reportes es la portada (sube
              de jerarquía, según el plan), Galería eran sección suelta del
              Sidebar sin relación visible con Reportes. Ahora comparte
              pestaña bajo /reportes/*. /reportes y /reportes/:periodo no
              cambian de path — solo ganan el shell — así que no necesitan
              redirect.
              v8.6.22 · Fase 24 — Coberturas se va de acá: no es contenido
              retrospectivo (no encaja con el período mensual de esta
              zona), es algo que el cliente necesita ver VENIR — se mudó a
              la zona "Plan" (ver PlanLayout más abajo). Redirect de
              /reportes/coberturas → /planes/coberturas para no romper
              links/favoritos viejos. */}
          <Route path="reportes" element={<RouteSuspense><ResultadosLayout /></RouteSuspense>}>
            <Route index               element={<Reportes />} />
            <Route path=":periodo"     element={<Reportes />} />
            <Route path="galeria"      element={<Galeria />} />
          </Route>
          <Route path="galeria"           element={<Navigate to="/reportes/galeria" replace />} />
          <Route path="reportes/coberturas" element={<Navigate to="/planes/coberturas" replace />} />
          <Route path="coberturas"        element={<Navigate to="/planes/coberturas" replace />} />

          {/* v8.5.46 · F1 · Piel Estudio (nav) · Pedidos ahora vive como
              pestaña de /tareas (zona "Trabajo") — este path viejo redirige
              conservando compatibilidad con links/favoritos existentes. */}
          {/* v8.6.23 · Fase 29 · antes usaba <Navigate> plano y perdía el
              query string — el CTA "Cargar un pedido extra" de Dashboard.jsx
              navega a /pedidos?nuevo=1 para autoabrir el formulario, y el
              parámetro se perdía acá antes de llegar a Pedidos.jsx. */}
          <Route path="pedidos"           element={<RedirectPreserveQuery to="/tareas/pedidos" />} />

          {/* v8.5.23 · F3 · zona "Cuenta" — Brand/Documentos/Equipo/Facturas/
              Configuración vivían repartidos en 3 grupos de nav sin relación
              entre sí; ninguno comparte datos con los demás, así que se
              consolidan bajo un solo shell con pestañas. Las rutas viejas
              quedan como redirect — /documentos en particular es el target
              de un tipo de resultado del buscador global (search), así que
              tiene que seguir resolviendo para siempre, no solo durante una
              ventana de migración. */}
          <Route path="cuenta" element={<RouteSuspense><CuentaLayout /></RouteSuspense>}>
            <Route index             element={<Navigate to="brand" replace />} />
            <Route path="brand"      element={<Brand />} />
            <Route path="documentos" element={<Documentos />} />
            <Route path="equipo"     element={<Equipo />} />
            <Route path="facturas"   element={<Facturas />} />
            <Route path="configuracion" element={<Configuracion />} />
          </Route>
          <Route path="facturas"          element={<Navigate to="/cuenta/facturas" replace />} />
          <Route path="equipo"            element={<Navigate to="/cuenta/equipo" replace />} />
          <Route path="brand"              element={<Navigate to="/cuenta/brand" replace />} />
          <Route path="documentos"        element={<Navigate to="/cuenta/documentos" replace />} />
          <Route path="configuracion"     element={<Navigate to="/cuenta/configuracion" replace />} />

          <Route path="aprobar"           element={<Navigate to="/tareas/aprobar" replace />} />
          {/* v8.5.26 · F3 · zona "Conversación" — el asistente IA vivía
              duplicado como página propia (/asistente) Y como FAB flotante
              global, mismo localStorage, mismo endpoint. Ahora es un modo
              dentro de /chat (Conversacion.jsx) — un solo destino. */}
          <Route path="asistente"         element={<Navigate to="/chat?modo=asistente" replace />} />
          <Route path="chat"              element={<RouteSuspense><Conversacion /></RouteSuspense>} />

          {/* v8.5.30 · F3 · zona "Plan" — Calendario y Feed Preview vivían
              como rutas top-level sin relación visible con /planes, pese a
              que las 3 muestran distintas vistas del mismo mes. Ahora
              comparten pestañas bajo /planes/*, mismo patrón que Resultados.
              Las rutas viejas redirigen conservando el query string —
              /feed-preview?periodo=2026-07 (defecto 02) y /calendario?dia=
              (MiniCalendar) siguen resolviendo desde afuera. */}
          <Route path="planes" element={<RouteSuspense><PlanLayout /></RouteSuspense>}>
            <Route index               element={<Planes />} />
            <Route path="calendario"   element={<Calendario />} />
            <Route path="feed"         element={<FeedPreview />} />
            {/* v8.6.22 · Fase 24 — mudado de /reportes/coberturas, ver
                comentario en la zona "Resultados" más arriba. */}
            <Route path="coberturas"   element={<Coberturas />} />
            <Route path="presentaciones"     element={<Presentaciones />} />
            <Route path="presentaciones/:id" element={<Presentaciones />} />
            <Route path=":grupoId"     element={<PlanDetalle />} />
          </Route>
          <Route path="calendario"     element={<RedirectPreserveQuery to="/planes/calendario" />} />
          <Route path="feed-preview"   element={<RedirectPreserveQuery to="/planes/feed" />} />
        </Route>
        <Route path="*" element={<Portal404 />} />
      </Routes>
    </AuthProvider>
    </ErrorBoundary>
  );
}
